import type {
  ConsoleMfaMethod,
  ConsoleMfaRow,
  PlayerConsoleMfaMethod,
  PlayerConsoleMfaRow,
} from '@/api/cmApi'
import { ROLE_LABEL, levelToRole } from '@/features/hierarchy/hierarchy'

/** TM → Master (levels 2–5). Player portal MFA is a separate audience tab. */
export function isConsoleMfaAgentLevel(level: number | null | undefined): boolean {
  return typeof level === 'number' && level >= 2 && level <= 5
}

export type ConsoleMfaConfirmIntent =
  | { kind: 'disable-all'; row: ConsoleMfaRow }
  | { kind: 'disable-method'; row: ConsoleMfaRow; method: ConsoleMfaMethod }
  | { kind: 'require-setup'; row: ConsoleMfaRow }
  | { kind: 'clear-setup'; row: ConsoleMfaRow }

export type PlayerConsoleMfaConfirmIntent =
  | { kind: 'disable-all'; row: PlayerConsoleMfaRow }
  | { kind: 'disable-method'; row: PlayerConsoleMfaRow; method: PlayerConsoleMfaMethod }
  | { kind: 'require-setup'; row: PlayerConsoleMfaRow }
  | { kind: 'clear-setup'; row: PlayerConsoleMfaRow }

export function methodLabel(m: ConsoleMfaMethod | PlayerConsoleMfaMethod): string {
  if (m === 'totp') return 'App'
  if (m === 'passkey') return 'Passkey'
  if (m === 'sms') return 'SMS'
  return 'Recovery'
}

export function methodTone(
  m: ConsoleMfaMethod | PlayerConsoleMfaMethod,
): 'brand' | 'info' | 'muted' {
  if (m === 'totp') return 'brand'
  if (m === 'passkey' || m === 'sms') return 'info'
  return 'muted'
}

/**
 * One-line agent label: drop duplicate displayName when it matches username;
 * show email (or short agent id) beside the name.
 */
export function agentLine(row: ConsoleMfaRow): { name: string; aside: string | null } {
  const name = (row.username || row.displayName || row.agentId).trim()
  const nameL = name.toLowerCase()
  const display = (row.displayName || '').trim()
  const email = (row.loginEmail || '').trim()
  if (display && display.toLowerCase() !== nameL) {
    return {
      name,
      aside: email && email.toLowerCase() !== display.toLowerCase() ? `${display} · ${email}` : display,
    }
  }
  if (email && email.toLowerCase() !== nameL) {
    return { name, aside: email }
  }
  return { name, aside: row.agentId.slice(0, 8) }
}

export function playerLine(row: PlayerConsoleMfaRow): { name: string; aside: string | null } {
  const name = (row.username || row.displayName || row.playerId).trim()
  const nameL = name.toLowerCase()
  const display = (row.displayName || '').trim()
  const email = (row.loginEmail || '').trim()
  if (display && display.toLowerCase() !== nameL) {
    return {
      name,
      aside: email && email.toLowerCase() !== display.toLowerCase() ? `${display} · ${email}` : display,
    }
  }
  if (email && email.toLowerCase() !== nameL) {
    return { name, aside: email }
  }
  return { name, aside: null }
}

export function confirmCopy(intent: ConsoleMfaConfirmIntent): {
  title: string
  body: string
  confirmLabel: string
  danger: boolean
} {
  const who = intent.row.username || intent.row.displayName
  const tier = ROLE_LABEL[levelToRole(intent.row.level)]
  if (intent.kind === 'disable-all') {
    return {
      title: 'Turn off all console 2FA?',
      body: `This removes every second factor (authenticator app, passkey, recovery codes) for ${who} (${tier}). They can sign in with password only until they set 2FA up again. This cannot be undone from their device.`,
      confirmLabel: 'Yes, turn off all 2FA',
      danger: true,
    }
  }
  if (intent.kind === 'disable-method') {
    const label =
      intent.method === 'totp'
        ? 'authenticator app'
        : intent.method === 'passkey'
          ? 'passkey'
          : 'recovery codes'
    return {
      title: `Remove ${label}?`,
      body: `Only the ${label} for ${who} (${tier}) will be removed. Other factors stay active. Confirm you intend to change this user’s sign-in security.`,
      confirmLabel: `Yes, remove ${label}`,
      danger: true,
    }
  }
  if (intent.kind === 'require-setup') {
    return {
      title: 'Require 2FA setup on next login?',
      body: `${who} (${tier}) must enroll an authenticator app the next time they sign in to the agent console. Existing factors are not removed.`,
      confirmLabel: 'Yes, require setup',
      danger: false,
    }
  }
  return {
    title: 'Clear forced 2FA setup?',
    body: `Remove the pending “set up authenticator” requirement for ${who} (${tier}). They can sign in without being forced to enroll.`,
    confirmLabel: 'Yes, clear requirement',
    danger: false,
  }
}

export function playerConfirmCopy(intent: PlayerConsoleMfaConfirmIntent): {
  title: string
  body: string
  confirmLabel: string
  danger: boolean
} {
  const who = intent.row.username || intent.row.displayName
  if (intent.kind === 'disable-all') {
    return {
      title: 'Turn off all player 2FA?',
      body: `This removes authenticator app / SMS factors and recovery codes for portal player ${who}, and revokes their open sessions. They can sign in with password only until they enroll again.`,
      confirmLabel: 'Yes, turn off all 2FA',
      danger: true,
    }
  }
  if (intent.kind === 'require-setup') {
    return {
      title: 'Require 2FA setup?',
      body: `CM cannot enroll Google Authenticator / SMS for ${who}. This marks them to enroll on the player site (Account → 2FA). They complete the QR / SMS themselves — same idea as agent “Require setup”.`,
      confirmLabel: 'Yes, require setup',
      danger: false,
    }
  }
  if (intent.kind === 'clear-setup') {
    return {
      title: 'Clear forced 2FA setup?',
      body: `Remove the pending enroll requirement for portal player ${who}.`,
      confirmLabel: 'Yes, clear requirement',
      danger: false,
    }
  }
  const label =
    intent.method === 'totp'
      ? 'authenticator app'
      : intent.method === 'sms'
        ? 'SMS'
        : 'recovery codes'
  return {
    title: `Remove ${label}?`,
    body: `Only the ${label} for portal player ${who} will be removed. Other factors stay active.`,
    confirmLabel: `Yes, remove ${label}`,
    danger: true,
  }
}
